import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { api, formatApiError } from "@/lib/api";
import { CONTACT_CATEGORIES, IMAGES } from "@/lib/content";

const inputCls =
  "w-full bg-transparent border-b border-ptf-primary/25 py-3 text-base outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", category: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact-submissions", form);
      setDone(true);
      toast.success(data.message || "Message received.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {done ? (
        <motion.div key="done" data-testid="contact-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-8">
          <span className="grid place-items-center h-14 w-14 rounded-full bg-ptf-accent text-ptf-primary mb-6"><Check className="h-7 w-7" /></span>
          <h3 className="font-display uppercase text-3xl tracking-tight text-ptf-primary">Message sent.</h3>
          <p className="mt-4 max-w-md text-ptf-text/75 leading-relaxed">
            Thank you for reaching out. We have received your message and will respond soon.
          </p>
          <button type="button" onClick={() => { setDone(false); setForm({ name: "", email: "", category: "", subject: "", message: "" }); }} className="mt-8 text-xs uppercase tracking-[0.18em] underline underline-offset-4 text-ptf-primary hover:text-ptf-secondary">
            Send another message
          </button>
        </motion.div>
      ) : (
        <motion.form key="form" onSubmit={submit} data-testid="contact-form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
          <div>
            <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">Name *</label>
            <input required data-testid="cf-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
          </div>
          <div>
            <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">Email *</label>
            <input required type="email" data-testid="cf-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
          </div>
          <div>
            <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">Enquiry Type</label>
            <select data-testid="cf-category" value={form.category} onChange={set("category")} className={inputCls}>
              <option value="">Select type</option>
              {CONTACT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">Subject</label>
            <input data-testid="cf-subject" value={form.subject} onChange={set("subject")} className={inputCls} placeholder="Subject" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">Message *</label>
            <textarea required rows={4} data-testid="cf-message" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="How can we help?" />
          </div>
          <div className="md:col-span-2">
            <button type="submit" data-testid="cf-submit" disabled={loading} className="group inline-flex items-center gap-2 bg-ptf-primary text-ptf-bg px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Sending" : "Send Message"}
            </button>
          </div>
        </motion.form>
      )}
    </AnimatePresence>
  );
}

export default function Contact() {
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const email = settings?.contact_email || "contact@planettrotterfoundation.com";

  return (
    <>
      <Seo title="Contact" path="/contact" description="Start a conversation with Planet Trotter Foundation." />
      <PageHero
        eyebrow="Contact"
        titleLines={["Start a", "conversation."]}
        intro="Partnerships, programmes, research or media. Tell us what you are working on."
        image={IMAGES.river}
      />

      <section className="bg-ptf-bg py-24 md:py-32">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="text-[0.7rem] uppercase tracking-[0.28em] text-ptf-primary/60 mb-6 flex items-center gap-3">
              <span className="h-px w-8 bg-ptf-primary/40 inline-block" /> How can we help
            </p>
            <ul className="space-y-2 mb-10">
              {CONTACT_CATEGORIES.map((c) => (
                <li key={c} className="font-display uppercase text-xl md:text-2xl tracking-tight text-ptf-primary/85">{c}</li>
              ))}
            </ul>

            <div className="space-y-5 pt-8 border-t border-ptf-primary/15">
              <a href={`mailto:${email}`} data-testid="contact-email" className="flex items-start gap-3 text-ptf-text/80 hover:text-ptf-primary transition-colors">
                <Mail className="h-5 w-5 mt-0.5 shrink-0 text-ptf-secondary" />
                <span className="break-all">{email}</span>
              </a>
              <div className="flex items-start gap-3 text-ptf-text/80">
                <MapPin className="h-5 w-5 mt-0.5 shrink-0 text-ptf-secondary" />
                <span>{settings?.address || "India"}</span>
              </div>
              {settings?.phone ? (
                <a href={`tel:${settings.phone.replace(/\s+/g, "")}`} data-testid="contact-phone" className="flex items-start gap-3 text-ptf-text/80 hover:text-ptf-primary transition-colors">
                  <Phone className="h-5 w-5 mt-0.5 shrink-0 text-ptf-secondary" />
                  <span>{settings.phone}</span>
                </a>
              ) : null}
            </div>
          </div>

          <div className="lg:col-span-8">
            <Reveal>
              <div className="bg-white border border-ptf-primary/10 p-7 md:p-10">
                <ContactForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
